import React, { useState, useRef } from 'react';
import { useLanguage } from '../context/LanguageContext';
import { useComparison } from '../context/ComparisonContext';
import { useTheme } from '../context/ThemeContext';
import { t } from '../translations/translations';
import { ReactComponent as SunIcon } from '../icons/sun.svg';
import { ReactComponent as RainIcon } from '../icons/rain.svg';
import { ReactComponent as SnowIcon } from '../icons/snow.svg';
import { ReactComponent as CloudyIcon } from '../icons/cloudy.svg';
import { ReactComponent as NightIcon } from '../icons/night.svg';
import { ReactComponent as TireSummerIcon } from '../icons/tire-summer.svg';
import { ReactComponent as TireWinterIcon } from '../icons/tire-winter.svg';
import { ReactComponent as TireAllSeasonIcon } from '../icons/tire-allseason.svg';
import TooltipPortal from './TooltipPortal';

const CarCard = ({ car }) => {
  const { darkMode } = useTheme();
  const { language } = useLanguage();
  const { addToComparison, removeFromComparison, isInComparison } = useComparison();
  const [showChemistryTooltip, setShowChemistryTooltip] = useState(false);
  const [showChargingTooltip, setShowChargingTooltip] = useState(false);
  const chemistryButtonRef = useRef(null);
  const chargingButtonRef = useRef(null);

  const inComparison = isInComparison(car.id);

  // Format date based on language
  const formatDate = (dateString) => {
    if (!dateString) return '';
    const date = new Date(dateString + 'T00:00:00'); // Add time to avoid timezone issues
    if (language === 'no') {
      return date.toLocaleDateString('nb-NO', { day: 'numeric', month: 'long', year: 'numeric' });
    } else {
      return date.toLocaleDateString('en-US', { month: 'long', day: 'numeric', year: 'numeric' });
    }
  };

  // Translate weather value
  const getWeatherLabel = (weather) => {
    const weatherMap = {
      'sun': 'rangeTest.sun',
      'rain': 'rangeTest.rain',
      'snow': 'rangeTest.snow',
      'cloudy': 'rangeTest.cloudy',
      'night': 'rangeTest.night'
    };
    return t(language, weatherMap[weather] || weather);
  };

  // Get tire icon based on tire type
  const getTireIcon = (tires) => {
    const tiresLower = tires.toLowerCase();
    if (tiresLower.includes('winter') || tiresLower.includes('vinter')) {
      return <TireWinterIcon className="w-5 h-5" />;
    } else if (tiresLower.includes('summer') || tiresLower.includes('sommer')) {
      return <TireSummerIcon className="w-5 h-5" />;
    } else {
      return <TireAllSeasonIcon className="w-5 h-5" />;
    }
  };

  // Weather-based styling
  const getWeatherStyles = (weather) => {
    switch (weather) {
      case 'sun':
        return {
          gradient: 'from-amber-400 via-orange-500 to-yellow-500',
          pattern: (
            <>
              <div className="absolute top-10 right-10 w-24 h-24 bg-yellow-300 rounded-full opacity-40 blur-2xl"></div>
              <div className="absolute top-5 right-5 w-20 h-20 bg-white rounded-full opacity-30"></div>
              <div className="absolute bottom-10 left-10 w-32 h-32 bg-orange-400 rounded-full opacity-20 blur-3xl"></div>
            </>
          ),
          icon: <SunIcon className="w-10 h-10" />,
          textColor: 'text-white'
        };
      case 'rain':
        return {
          gradient: 'from-blue-500 via-cyan-500 to-blue-700',
          pattern: (
            <>
              <div className="absolute top-0 left-0 w-full h-full overflow-hidden">
                {[...Array(20)].map((_, i) => (
                  <div
                    key={i}
                    className="absolute w-0.5 bg-white opacity-30"
                    style={{
                      left: `${Math.random() * 100}%`,
                      top: `${Math.random() * -20}%`,
                      height: `${15 + Math.random() * 10}px`,
                      animation: `fall ${1 + Math.random() * 2}s linear infinite`,
                      animationDelay: `${Math.random() * 2}s`
                    }}
                  ></div>
                ))}
              </div>
              <div className="absolute top-5 right-8 w-28 h-16 bg-slate-400 rounded-full opacity-40 blur-xl"></div>
              <div className="absolute top-8 right-16 w-20 h-12 bg-slate-300 rounded-full opacity-30 blur-lg"></div>
            </>
          ),
          icon: <RainIcon className="w-10 h-10" />,
          textColor: 'text-white'
        };
      case 'snow':
        return {
          gradient: 'from-blue-300 via-cyan-200 to-slate-300',
          pattern: (
            <>
              <div className="absolute top-0 left-0 w-full h-full overflow-hidden">
                {[...Array(15)].map((_, i) => (
                  <div
                    key={i}
                    className="absolute w-2 h-2 bg-white rounded-full opacity-70"
                    style={{
                      left: `${Math.random() * 100}%`,
                      top: `${Math.random() * -20}%`,
                      animation: `snowfall ${3 + Math.random() * 3}s linear infinite`,
                      animationDelay: `${Math.random() * 3}s`
                    }}
                  ></div>
                ))}
              </div>
              <div className="absolute bottom-0 left-0 w-full h-8 bg-white opacity-40"></div>
            </>
          ),
          icon: <SnowIcon className="w-10 h-10" />,
          textColor: 'text-slate-800'
        };
      case 'cloudy':
        return {
          gradient: 'from-gray-400 via-slate-500 to-gray-600',
          pattern: (
            <>
              <div className="absolute top-10 right-12 w-32 h-20 bg-gray-300 rounded-full opacity-50 blur-xl"></div>
              <div className="absolute top-14 right-6 w-24 h-16 bg-slate-200 rounded-full opacity-40 blur-lg"></div>
              <div className="absolute bottom-12 left-8 w-28 h-18 bg-gray-400 rounded-full opacity-40 blur-xl"></div>
              <div className="absolute bottom-16 left-16 w-20 h-14 bg-slate-300 rounded-full opacity-30 blur-lg"></div>
            </>
          ),
          icon: <CloudyIcon className="w-10 h-10" />,
          textColor: 'text-white'
        };
      case 'night':
        return {
          gradient: 'from-slate-900 via-indigo-900 to-slate-800',
          pattern: (
            <>
              <div className="absolute top-0 left-0 w-full h-full overflow-hidden">
                {[...Array(25)].map((_, i) => (
                  <div
                    key={i}
                    className="absolute w-1 h-1 bg-white rounded-full"
                    style={{
                      left: `${Math.random() * 100}%`,
                      top: `${Math.random() * 100}%`,
                      opacity: 0.3 + Math.random() * 0.6
                    }}
                  ></div>
                ))}
              </div>
              <div className="absolute top-8 right-10 w-16 h-16 bg-amber-100 rounded-full opacity-80 shadow-[0_0_40px_10px_rgba(254,243,199,0.4)]"></div>
              <div className="absolute top-8 right-10 w-16 h-16 rounded-full" style={{ boxShadow: 'inset -10px 6px 0 0 rgba(30,41,59,0.35)' }}></div>
            </>
          ),
          icon: <NightIcon className="w-10 h-10" />,
          textColor: 'text-white'
        };
      default:
        return {
          gradient: 'from-indigo-600 via-purple-600 to-pink-500',
          pattern: null,
          icon: null,
          textColor: 'text-white'
        };
    }
  };

  const weatherStyle = getWeatherStyles(car.weather);

  const handleComparisonToggle = () => {
    if (inComparison) {
      removeFromComparison(car.id);
    } else {
      addToComparison(car);
    }
  };

  return (
    <div className="relative h-full">
      <div className={`h-full flex flex-col rounded-2xl shadow-2xl overflow-hidden transform transition-all duration-300 hover:scale-105 border ${
        darkMode
          ? 'bg-slate-800 hover:shadow-teal-500/50 border-teal-500 border-opacity-20'
          : 'bg-white hover:shadow-teal-600/30 border-stone-200'
      }`}>
        <style>{`
          @keyframes fall {
            to {
              transform: translateY(400px);
              opacity: 0;
            }
          }
          @keyframes snowfall {
            to {
              transform: translateY(400px) rotate(360deg);
              opacity: 0;
            }
          }
        `}</style>

        {/* Comparison Button */}
        <button
          onClick={handleComparisonToggle}
          className={`absolute top-4 right-4 z-20 flex items-center gap-2 px-4 py-2 rounded-lg font-semibold text-sm transition-all duration-200 shadow-lg ${
            inComparison
              ? 'bg-teal-500 text-slate-900 hover:bg-teal-400'
              : darkMode
                ? 'bg-slate-700 bg-opacity-90 text-gray-200 hover:bg-slate-600 border border-teal-500 border-opacity-30'
                : 'bg-white bg-opacity-90 text-stone-700 hover:bg-stone-100 border border-stone-300'
          }`}
        >
          <svg
            className="w-5 h-5"
            fill="none"
            stroke="currentColor"
            viewBox="0 0 24 24"
          >
            {inComparison ? (
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M5 13l4 4L19 7"
              />
            ) : (
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M12 4v16m8-8H4"
              />
            )}
          </svg>
          {t(language, inComparison ? 'card.comparing' : 'card.compare')}
        </button>

        {/* Hero Section with KM Score - Weather Themed */}
        <div className={`relative bg-gradient-to-br ${weatherStyle.gradient} p-8 pt-16 ${weatherStyle.textColor} overflow-hidden`}>
        {weatherStyle.pattern}

        <div className="relative z-10">
          {/* Weather, Season, Temperature, and Tires Header */}
          <div className="mb-4">
            <div className="flex items-center justify-between mb-3">
              <div className="flex items-center space-x-2">
                <div className="bg-white bg-opacity-20 backdrop-blur-sm rounded-full p-2">
                  {weatherStyle.icon}
                </div>
                <span className="px-4 py-1 bg-white bg-opacity-20 rounded-full text-xs font-semibold tracking-wider uppercase backdrop-blur-sm">
                  {getWeatherLabel(car.weather)}
                </span>
                <span className={`px-4 py-1 rounded-full text-xs font-bold tracking-wider uppercase ${
                  car.season === 'winter'
                    ? 'bg-cyan-500 bg-opacity-30 text-white border border-cyan-400 border-opacity-50'
                    : 'bg-amber-500 bg-opacity-30 text-white border border-amber-400 border-opacity-50'
                }`}>
                  {car.season === 'winter' ? t(language, 'card.winter') : t(language, 'card.summer')}
                </span>
              </div>
              <div className="text-right">
                <div className="text-4xl font-black drop-shadow-lg">
                  {car.temperature}°C
                </div>
                <div className="text-xs font-medium opacity-80 uppercase tracking-wide">
                  {t(language, 'card.testTemp')}
                </div>
              </div>
            </div>
            <div className="bg-white bg-opacity-15 backdrop-blur-sm rounded-lg px-4 py-2 border border-white border-opacity-20">
              <div className="flex items-center justify-between">
                <span className="text-xs font-bold opacity-80 uppercase tracking-wide">{t(language, 'card.tires')}:</span>
                <div className="flex items-center gap-2">
                  {getTireIcon(car.tires)}
                  <span className="text-sm font-bold">{car.tires}</span>
                </div>
              </div>
            </div>
          </div>

          <h2 className="text-3xl font-bold mb-1">{car.make}</h2>
          <p className="text-xl font-light opacity-95 mb-2">{car.model}</p>
          <p className="text-base opacity-75 mb-6">{car.year} • {t(language, 'card.tested')} {formatDate(car.testDate)}</p>

          {/* Large KM Number */}
          <div className="text-center py-6">
            <div className="inline-block">
              <div className="text-7xl font-black leading-none mb-2 drop-shadow-lg">
                {car.rangeKm.toLocaleString()}
              </div>
              <div className="text-lg font-semibold tracking-widest uppercase opacity-90">
                {t(language, 'card.kilometers')}
              </div>
              <div className="text-sm font-medium opacity-90 mt-2">
                {t(language, 'card.addedIn15')}
              </div>
              <div className="text-sm font-medium opacity-75 mt-1">
                ~{Math.round((car.rangeKm / 120) * 60)} {t(language, 'card.minutesOnRoad')}
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Information Grid */}
      <div className="p-6 flex flex-col flex-grow">
        <div className="grid grid-cols-2 gap-4">
          {/* Battery Size */}
          <div className={`rounded-xl p-4 border ${
            darkMode
              ? 'bg-gradient-to-br from-slate-700 to-slate-600 border-teal-500 border-opacity-30'
              : 'bg-stone-100 border-stone-200'
          }`}>
            <div className={`text-xs font-semibold uppercase tracking-wide mb-1 ${darkMode ? 'text-gray-400' : 'text-stone-500'}`}>
              {t(language, 'card.battery')}
            </div>
            <div className={`text-2xl font-bold ${darkMode ? 'text-gray-100' : 'text-stone-900'}`}>
              {car.batterySize}
              <span className={`text-sm font-normal ml-1 ${darkMode ? 'text-gray-300' : 'text-stone-600'}`}>kWh</span>
            </div>
            {car.batteryChemistry && (
              <div className={`mt-2 text-sm flex items-center gap-1 relative ${darkMode ? 'text-gray-300' : 'text-stone-600'}`}>
                {car.batteryChemistry}
                {car.batteryChemistryInfo && (
                  <>
                    <button
                      ref={chemistryButtonRef}
                      aria-label={t(language, 'card.moreInfo')}
                      onMouseEnter={() => setShowChemistryTooltip(true)}
                      onMouseLeave={() => setShowChemistryTooltip(false)}
                      onClick={(e) => {
                        e.stopPropagation();
                        setShowChemistryTooltip(!showChemistryTooltip);
                      }}
                      className={darkMode ? 'text-teal-400 hover:text-teal-300 transition-colors' : 'text-teal-600 hover:text-teal-500 transition-colors'}
                    >
                      <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 20 20">
                        <path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7-4a1 1 0 11-2 0 1 1 0 012 0zM9 9a1 1 0 000 2v3a1 1 0 001 1h1a1 1 0 100-2v-3a1 1 0 00-1-1H9z" clipRule="evenodd" />
                      </svg>
                    </button>
                    <TooltipPortal show={showChemistryTooltip} buttonRef={chemistryButtonRef}>
                      {car.batteryChemistryInfo}
                    </TooltipPortal>
                  </>
                )}
              </div>
            )}
          </div>

          {/* Max Charging Speed */}
          <div className="bg-gradient-to-br from-teal-600 to-teal-700 rounded-xl p-4 border border-teal-400 border-opacity-50 relative">
            <div className="text-xs font-semibold text-teal-100 uppercase tracking-wide mb-1 flex items-center gap-1">
              {t(language, 'card.maxCharging')}
              {car.maxChargingSpeedInfo && (
                <>
                  <button
                    ref={chargingButtonRef}
                    aria-label={t(language, 'card.moreInfo')}
                    onMouseEnter={() => setShowChargingTooltip(true)}
                    onMouseLeave={() => setShowChargingTooltip(false)}
                    onClick={(e) => {
                      e.stopPropagation();
                      setShowChargingTooltip(!showChargingTooltip);
                    }}
                    className="text-teal-200 hover:text-white transition-colors"
                  >
                    <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 20 20">
                      <path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7-4a1 1 0 11-2 0 1 1 0 012 0zM9 9a1 1 0 000 2v3a1 1 0 001 1h1a1 1 0 100-2v-3a1 1 0 00-1-1H9z" clipRule="evenodd" />
                    </svg>
                  </button>
                  <TooltipPortal show={showChargingTooltip} buttonRef={chargingButtonRef}>
                    {car.maxChargingSpeedInfo}
                  </TooltipPortal>
                </>
              )}
            </div>
            <div className="text-2xl font-bold text-white">
              {car.maxChargingSpeed}
              <span className="text-sm font-normal text-teal-100 ml-1">kW</span>
            </div>
          </div>
        </div>

        {/* Additional Details */}
        <div className={`mt-6 pt-6 border-t ${darkMode ? 'border-teal-500 border-opacity-20' : 'border-stone-200'}`}>
          <div className="grid grid-cols-2 gap-4 text-sm">
            {[
              ['card.drive', car.drivetrain],
              ['card.wltpRange', `${car.wltpRange} km`],
              ['card.power', `${car.power} hp`],
              ['card.weight', `${car.weight} kg`],
            ].map(([key, value]) => (
              <div key={key}>
                <span className={`font-medium ${darkMode ? 'text-gray-400' : 'text-stone-500'}`}>{t(language, key)}:</span>
                <span className={`ml-2 font-semibold ${darkMode ? 'text-gray-100' : 'text-stone-900'}`}>{value}</span>
              </div>
            ))}
          </div>
        </div>

        {/* YouTube Link — pinned to the card bottom so all cards line up */}
        {car.youtubeUrl && (
          <div className="mt-auto pt-4">
            <a
              href={car.youtubeUrl}
              target="_blank"
              rel="noopener noreferrer"
              className="flex items-center justify-center gap-2 w-full px-4 py-3 bg-red-600 hover:bg-red-700 text-white font-semibold rounded-lg transition-colors duration-200 shadow-lg hover:shadow-red-500/50"
            >
              <svg className="w-6 h-6" fill="currentColor" viewBox="0 0 24 24">
                <path d="M23.498 6.186a3.016 3.016 0 0 0-2.122-2.136C19.505 3.545 12 3.545 12 3.545s-7.505 0-9.377.505A3.017 3.017 0 0 0 .502 6.186C0 8.07 0 12 0 12s0 3.93.502 5.814a3.016 3.016 0 0 0 2.122 2.136c1.871.505 9.376.505 9.376.505s7.505 0 9.377-.505a3.015 3.015 0 0 0 2.122-2.136C24 15.93 24 12 24 12s0-3.93-.502-5.814zM9.545 15.568V8.432L15.818 12l-6.273 3.568z"/>
              </svg>
              Se test på YouTube
            </a>
          </div>
        )}
      </div>
    </div>
    </div>
  );
};

export default CarCard;
